/**
 * Renderer, camera, lighting and pointer picking.
 *
 * The camera orbits a fixed target at the centre of the board. Orbit control
 * is hand-rolled rather than pulled from three's examples so the build has no
 * addon dependency and so a "flip to the other side" tween is trivial.
 */

import * as THREE from 'three';

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const TAU = Math.PI * 2;

export class SceneView {
  constructor(canvas) {
    this.canvas = canvas;
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: 'high-performance'
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x0a0d14);
    this.scene.fog = new THREE.Fog(0x0a0d14, 18, 38);

    this.camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
    this.target = new THREE.Vector3(0, 0.4, 0);

    // Spherical orbit state.
    this.azimuth = 0;
    this.polar = 0.95;
    this.radius = 13.5;
    this.targetAzimuth = 0;
    this.targetPolar = 0.95;
    this.targetRadius = 13.5;
    // How close and how far the wheel is allowed to bring the camera. Tuned
    // for a whole 8x8 board by default — 7 is roughly where the near board
    // edge would start clipping through the camera, 24 where the board
    // becomes too small to read. A caller inspecting one piece rather than a
    // board (the model editor) can widen these after construction.
    this.minRadius = 7;
    this.maxRadius = 24;

    this.buildLights();
    this.buildEnvironment();
    this.bindPointer();

    this.raycaster = new THREE.Raycaster();
    this.pointer = new THREE.Vector2();
    this.resize();
    window.addEventListener('resize', () => this.resize());
  }

  buildLights() {
    this.scene.add(new THREE.HemisphereLight(0x9fb6d6, 0x2a2418, 0.55));

    const key = new THREE.DirectionalLight(0xfff0d8, 2.1);
    key.position.set(6, 12, 7);
    key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    key.shadow.camera.near = 1;
    key.shadow.camera.far = 40;
    const d = 8;
    Object.assign(key.shadow.camera, { left: -d, right: d, top: d, bottom: -d });
    key.shadow.bias = -0.0008;
    key.shadow.normalBias = 0.02;
    this.scene.add(key);
    this.keyLight = key;

    // A soft fill that rides with the camera, so whichever side you orbit to
    // is never left in silhouette.
    this.cameraFill = new THREE.PointLight(0xbcd0f0, 22, 26, 2);
    this.scene.add(this.cameraFill);

    const rim = new THREE.DirectionalLight(0x5f8fd0, 0.8);
    rim.position.set(-7, 6, -8);
    this.scene.add(rim);

    // Four braziers frame the board, one pair at each end, and give the
    // scene its warm accent.
    for (const z of [-5.6, 5.6]) {
      for (const x of [-5.6, 5.6]) {
        const brazier = new THREE.PointLight(0xff8a3c, 12, 14, 2);
        brazier.position.set(x, 2.2, z);
        this.scene.add(brazier);

        const bowl = new THREE.Mesh(
          new THREE.CylinderGeometry(0.32, 0.16, 0.3, 12),
          new THREE.MeshStandardMaterial({ color: 0x2a2a30, roughness: 0.8, metalness: 0.5 })
        );
        bowl.position.set(x, 2.0, z);
        const stand = new THREE.Mesh(
          new THREE.CylinderGeometry(0.07, 0.16, 1.9, 10),
          new THREE.MeshStandardMaterial({ color: 0x24242a, roughness: 0.85, metalness: 0.5 })
        );
        stand.position.set(x, 0.95, z);
        const flame = new THREE.Mesh(
          new THREE.SphereGeometry(0.2, 10, 10),
          new THREE.MeshBasicMaterial({ color: 0xffb257 })
        );
        flame.position.set(x, 2.22, z);
        this.scene.add(bowl, stand, flame);
        (this.flames ??= []).push({ mesh: flame, light: brazier, phase: Math.random() * TAU });
      }
    }
  }

  buildEnvironment() {
    const floor = new THREE.Mesh(
      new THREE.CircleGeometry(26, 48),
      new THREE.MeshStandardMaterial({ color: 0x14161e, roughness: 0.95 })
    );
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -0.26;
    floor.receiveShadow = true;
    this.scene.add(floor);
  }

  bindPointer() {
    const canvas = this.canvas;
    let dragging = false;
    let moved = 0;
    let last = { x: 0, y: 0 };
    // Multi-touch, tracked by pointerId so a second finger can drive
    // pinch-zoom independently of the single-pointer orbit above — this
    // only engages once a second pointer actually joins, so a mouse or a
    // single touch never touches this path.
    const pointers = new Map();
    let pinchDist = 0;
    const pinchDistance = () => {
      const [a, b] = [...pointers.values()];
      return Math.hypot(a.x - b.x, a.y - b.y);
    };

    canvas.addEventListener('pointerdown', (e) => {
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      canvas.setPointerCapture(e.pointerId);
      if (pointers.size === 2) {
        // A second finger joining mid-drag ends the orbit, not stacks a
        // pinch on top of it — moving the camera two ways from one gesture
        // reads as broken, not as two features.
        dragging = false;
        pinchDist = pinchDistance();
        return;
      }
      if (pointers.size > 2) return;
      dragging = true;
      moved = 0;
      last = { x: e.clientX, y: e.clientY };
      // Fired on press rather than release so that skipping a battle does not
      // also consume the click as a move; `onClick` still fires on pointerup.
      this.onPointerDown?.();
    });

    canvas.addEventListener('pointermove', (e) => {
      if (pointers.has(e.pointerId)) pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size >= 2) {
        const d = pinchDistance();
        // Fingers spreading (d growing) should feel like moving in, the
        // same direction wheel's negative deltaY already zooms.
        this.targetRadius = clamp(this.targetRadius - (d - pinchDist) * 0.02, this.minRadius, this.maxRadius);
        pinchDist = d;
        return;
      }
      if (!dragging) return;
      const dx = e.clientX - last.x;
      const dy = e.clientY - last.y;
      moved += Math.abs(dx) + Math.abs(dy);
      last = { x: e.clientX, y: e.clientY };
      this.targetAzimuth -= dx * 0.006;
      this.targetPolar = clamp(this.targetPolar - dy * 0.005, 0.18, 1.45);
    });

    const endDrag = (e) => {
      pointers.delete(e.pointerId);
      canvas.releasePointerCapture?.(e.pointerId);
      if (!dragging) return;
      dragging = false;
      // A gesture under the slop threshold counts as a click, not an orbit.
      if (moved < 6) this.onClick?.(this.pickAt(e));
    };
    canvas.addEventListener('pointerup', endDrag);
    canvas.addEventListener('pointercancel', endDrag);

    canvas.addEventListener('wheel', (e) => {
      e.preventDefault();
      this.targetRadius = clamp(this.targetRadius + e.deltaY * 0.012, this.minRadius, this.maxRadius);
    }, { passive: false });
  }

  /** Normalised device coordinates for a pointer event. */
  pickAt(event) {
    const rect = this.canvas.getBoundingClientRect();
    this.pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.pointer, this.camera);
    return this.raycaster;
  }

  /** Swings the camera round to sit behind the given colour. */
  faceSide(color, immediate = false) {
    this.targetAzimuth = color === 'w' ? 0 : Math.PI;
    if (immediate) this.azimuth = this.targetAzimuth;
  }

  resize() {
    const w = this.canvas.clientWidth || window.innerWidth;
    const h = this.canvas.clientHeight || window.innerHeight;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  update(dt, elapsed) {
    // Ease the orbit toward its target, taking the short way round.
    let delta = this.targetAzimuth - this.azimuth;
    while (delta > Math.PI) delta -= TAU;
    while (delta < -Math.PI) delta += TAU;
    const k = 1 - Math.pow(0.0015, dt);
    this.azimuth += delta * k;
    this.polar += (this.targetPolar - this.polar) * k;
    this.radius += (this.targetRadius - this.radius) * k;

    const sinP = Math.sin(this.polar);
    this.camera.position.set(
      this.target.x + this.radius * sinP * Math.sin(this.azimuth),
      this.target.y + this.radius * Math.cos(this.polar),
      this.target.z + this.radius * sinP * Math.cos(this.azimuth)
    );
    this.camera.lookAt(this.target);
    this.cameraFill.position.copy(this.camera.position).setY(this.camera.position.y + 2);

    for (const flame of this.flames ?? []) {
      const flicker = 0.85 + Math.sin(elapsed * 9 + flame.phase) * 0.1 +
        Math.sin(elapsed * 23 + flame.phase) * 0.05;
      flame.mesh.scale.set(flicker, flicker * 1.25, flicker);
      flame.light.intensity = 10 + flicker * 4;
    }
  }

  render() {
    this.renderer.render(this.scene, this.camera);
  }
}
